import { useEffect, useState } from "react";
import { useTheme } from "./context/ThemeProvider";
import { initializeConfigFromAPI } from "./api/Secret/Secret";
import FeedbackBanner from "./components/Feedback/FeedbackBanner";
import { STATUS } from "./constants/const";
import AppRouter from "./router/Router";
import CacheStatus from "./components/Debug/CacheStatus";
import Loader from "./components/Loader/Loader";
import UserAuthStore from "./store/userStore";
import { getModeAuth } from "./utils/sparkApi/getModeAuth";
import { useGetAIModels } from "./api/queryHooks";
import logger from "./services/Logger";
import { getSessionToken, markAuthSessionCurrent } from "./services/AuthSession";

let configInitializationPromise = null;

function initializeConfigOnce() {
  if (!configInitializationPromise) {
    configInitializationPromise = new Promise((resolve, reject) => {
      initializeConfigFromAPI(resolve, () => {
        reject(new Error("Configuration initialization failed"));
      });
    }).catch((error) => {
      configInitializationPromise = null;
      throw error;
    });
  }

  return configInitializationPromise;
}

function InitApp() {
  const [status, setStatus] = useState(STATUS.LOADING);
  const { loadRemoteTheme } = useTheme();
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  
  // Prefetch AI models early to cache them for MessageFooter/ModelBadge
  useGetAIModels();

  useEffect(() => {
    let isMounted = true;

    initializeConfigOnce().then(
      () => {
        if (isMounted) {
          setStatus(STATUS.SUCCESS);
        }
      },
      () => {
        if (isMounted) {
          setStatus(STATUS.ERROR);
        }
      }
    );

    return () => {
      isMounted = false;
    };
  }, []);

  // Sync theme from DB once user identity is known
  useEffect(() => {
    if (user?.uid && orgId) {
      loadRemoteTheme(orgId, user.uid);
    }
  }, [user?.uid, orgId, loadRemoteTheme]);

  // Migration check: Upgrade old sessions to include admin privileges
  useEffect(() => {
    const authVersion = localStorage.getItem('auth_version');
    
    // If user is logged in and has auth version
    if (user?.uid && orgId) {
      if (authVersion !== '2') {
        const sessionToken = getSessionToken();
        // Upgrade old session (version 1 or missing)
        getModeAuth().then(() => {
          markAuthSessionCurrent(sessionToken);
        }).catch(err => {
          logger.error('InitApp', 'Failed to upgrade session', { error: err.message });
        });
      } else {
        // Version 2 session - restore admin privileges
        getModeAuth().catch(err => {
          logger.error('InitApp', 'Failed to restore admin privileges', { error: err.message });
        });
      }
    }
  }, [user, orgId]);

  if (status === STATUS.LOADING) {
    return (
      <div className="h-screen flex items-center justify-center dark:bg-fill-page-surface-dark bg-fill-hex-f5f5f5">
        <Loader variant="spinner" />
      </div>
    );
  }

  if (status === STATUS.ERROR)
    return <div>Something Went Wrong (Secret Not Found)</div>;

  return (
    <>
      <AppRouter />
      {process.env.REACT_APP_SHOW_CACHE_DEBUG === 'true' && (
        <CacheStatus show={true} />
      )}
      <FeedbackBanner />
    </>
  );
}

export default InitApp;
